import { CheckCircle, type Icon, Info, Warning, WarningCircle } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { cx } from "@/lib/cx";

export type CalloutTone = "info" | "warning" | "danger" | "success";

const tones: Record<CalloutTone, { box: string; icon: Icon }> = {
  info: { box: "border-accent-soft-fg/25 bg-accent-soft text-accent-soft-fg", icon: Info },
  warning: { box: "border-warning-soft-fg/30 bg-warning-soft text-warning-soft-fg", icon: Warning },
  danger: { box: "border-danger-soft-fg/30 bg-danger-soft text-danger-soft-fg", icon: WarningCircle },
  success: { box: "border-success-soft-fg/30 bg-success-soft text-success-soft-fg", icon: CheckCircle },
};

/**
 * Static, in-flow notice for something the reader must know before acting (a secret shown once, a
 * policy consequence, a blocked action). The tone only reinforces: the icon and the text carry the
 * meaning. Use `InlineError` for failures with retry and toasts for transient confirmations.
 *
 *   <Callout tone="warning" title="Copy it now">You will not see it again.</Callout>
 */
export function Callout({
  tone = "info",
  title,
  children,
  actions,
  className,
  role,
}: {
  tone?: CalloutTone;
  title?: string;
  children?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** "alert" or "status" when the notice appears after an action and must be announced. */
  role?: "alert" | "status";
}) {
  const { box, icon: I } = tones[tone];
  return (
    <div
      role={role}
      className={cx("flex items-start gap-2.5 rounded-control border px-3 py-2.5 text-sm", box, className)}
    >
      <I size={16} weight="fill" aria-hidden="true" className="mt-0.5 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className="text-pretty">{children}</div> : null}
        {actions ? <div className="mt-1 flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}
